import type { QueryClient, QueryKey } from '@/api/zustandQuery';
import type { Principal } from './client';

const STORAGE_KEY = 'mcp-queue-reload-v1';
const TTL = 30_000;
const INTAKE_TTL = 5 * 60_000;
const MAX_BYTES = 1_000_000;
const MAX_ENTRIES = 4;
const QUEUES = new Set(['image-builder', 'video-slideshow-builder', 'content-workbench', 'media-review']);
type Entry = { key: QueryKey; data: unknown; updatedAt: number };

const isIntake = (key: QueryKey) => key[0] === 'page' && key[1] === 'listing-intake' && key[3] === true;
const object = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value)
  ? value as Record<string, unknown> : {};
const pick = (value: Record<string, unknown>, keys: string[]) => Object.fromEntries(keys
  .filter(key => value[key] !== undefined).map(key => [key, value[key]]));

/** Display-only rows: no supplier forms/history, and edits still hydrate native detail. */
function intakePreview(data: unknown) {
  const page = object(data);
  if (!Array.isArray(page.records) || !Array.isArray(page.index) || page.index.length > 10_000 ||
      (!page.records.length && page.complete !== true && page.reloadPreview !== true)) return undefined;
  const compactRow = (entry: unknown, thumbnail = false): Record<string,unknown> => {
    const source = object(entry), facts = object(object(page.reloadFacts)[String(source.id)]);
    const row = { ...source, ...facts }, metadata = { ...object(source.metadata), ...object(facts.metadata) };
    const compactMetadata = pick(metadata, ['displayStatus','kind','retailPrice','compareAt','moq','stock',
      'images','translations','aiRefined','hasRefinementHistory','compliance']);
    if (typeof metadata.description === 'string') compactMetadata.description = metadata.description.slice(0,120);
    if (thumbnail) Object.assign(compactMetadata, pick(metadata, ['primaryImageUrl']));
    if (metadata.aiRefineRun) compactMetadata.aiRefineRun = pick(object(metadata.aiRefineRun), ['id','jobId','status']);
    if (metadata.aiEnrichment) compactMetadata.aiEnrichment = pick(object(metadata.aiEnrichment), ['imageStatus','textStatus']);
    return { ...pick(row, ['id','title','marketplace','category','currency','sku','status','updatedAt','backendVersion','_count']),
      queueSummary: true, metadata: compactMetadata };
  };
  const index = page.index.map(entry => compactRow(entry));
  if (index.some(row => typeof row.id !== 'string' || !row.id) || new Set(index.map(row => row.id)).size !== index.length) return undefined;
  const ids = new Set(index.map(row => row.id));
  const records = page.records.slice(0,20).map(entry => compactRow(entry,true));
  if (records.some(row => !ids.has(row.id))) return undefined;
  return { records, index, complete: false, reloadPreview: true };
}

export function clearQueueReloadCache(storage?: Storage) {
  try { (storage ?? window.sessionStorage).removeItem(STORAGE_KEY); } catch { /* Storage is optional. */ }
}

/** Called only after a fresh /me response verifies identity, session and grants. */
export function startQueueReloadCache(client: QueryClient, principal: Principal, storage?: Storage) {
  try { storage ??= window.sessionStorage; } catch { return () => {}; }
  if (!principal.workspaceId || !principal.id || !principal.sessionId) {
    clearQueueReloadCache(storage);
    return () => {};
  }
  const scope = [principal.workspaceId, principal.id, principal.sessionId];
  const identity = JSON.stringify([scope, principal.roles, principal.permissions, principal.scope]);
  const allowed = (key: QueryKey) => key[0] === 'page' && (QUEUES.has(String(key[1])) || isIntake(key)) &&
    JSON.stringify(key.slice(-3)) === JSON.stringify(scope);
  const fresh = (time: number, key: QueryKey) => Number.isFinite(time) && time <= Date.now() &&
    Date.now() - time < (isIntake(key) ? INTAKE_TTL : TTL);
  const complete = (data: unknown) => {
    if (!data || typeof data !== 'object') return false;
    const page = data as { batch?: { nextOffset?: unknown; total?: number }; listings?: unknown[]; listingIndex?: unknown[] };
    return page.batch?.nextOffset === null && Array.isArray(page.listings) && Array.isArray(page.listingIndex) &&
      page.batch.total === page.listingIndex.length && page.listings.length === page.listingIndex.length;
  };
  const entries = new Map<string, Entry>();
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw && raw.length <= MAX_BYTES) {
      const saved = JSON.parse(raw) as { identity?: unknown; entries?: Entry[] };
      if (saved.identity === identity && Array.isArray(saved.entries)) {
        for (const entry of saved.entries.slice(-MAX_ENTRIES)) {
          if (Array.isArray(entry.key) && allowed(entry.key) && fresh(entry.updatedAt, entry.key) &&
              (isIntake(entry.key) ? object(entry.data).reloadPreview === true && intakePreview(entry.data) : complete(entry.data))) {
            if (isIntake(entry.key)) entry.data = intakePreview(entry.data);
            entries.set(JSON.stringify(entry.key), entry);
            // Preserve the original age: repeated reloads never renew freshness.
            client.setQueryData(entry.key, entry.data, { updatedAt: entry.updatedAt });
            // Restore usable rows immediately, then confirm current native
            // readiness; a reload preview is never a fresh approval decision.
            void client.invalidateQueries({queryKey:entry.key,exact:true,refetchType:'none'});
          }
        }
      }
    }
  } catch { /* Malformed or unavailable storage cannot prevent authentication. */ }

  const save = () => {
    for (const [key, entry] of entries) if (!fresh(entry.updatedAt, entry.key)) entries.delete(key);
    while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
    try {
      let raw = JSON.stringify({ identity, entries: [...entries.values()] });
      while (raw.length > MAX_BYTES && entries.size) {
        entries.delete(entries.keys().next().value!);
        raw = JSON.stringify({ identity, entries: [...entries.values()] });
      }
      if (entries.size) storage!.setItem(STORAGE_KEY, raw);
      else storage!.removeItem(STORAGE_KEY);
    } catch { clearQueueReloadCache(storage); }
  };
  save();
  const unsubscribeQueries = client.getQueryCache().subscribe(event => {
    const query = event.query;
    if (!allowed(query.queryKey)) return;
    const key = JSON.stringify(query.queryKey);
    if (event.type === 'removed' || query.state.isInvalidated || query.state.status === 'error') {
      entries.delete(key); save(); return;
    }
    const intake = isIntake(query.queryKey);
    const snapshot = intake ? intakePreview(query.state.data) : query.state.data;
    if (event.type === 'updated' && event.action.type === 'success' && (intake || query.state.fetchStatus === 'idle') &&
      (intake ? snapshot !== undefined : complete(snapshot)) && fresh(query.state.dataUpdatedAt, query.queryKey) &&
      !client.isMutating()) {
      entries.delete(key);
      entries.set(key, { key: query.queryKey, data: snapshot, updatedAt: query.state.dataUpdatedAt });
      save();
    }
  });
  const unsubscribeMutations = client.getMutationCache().subscribe(event => {
    if (event.type === 'updated' && event.action.type === 'pending') {
      // A mutation can alter approval/rights/source state. Restore only fresh reads afterwards.
      entries.clear(); clearQueueReloadCache(storage);
    }
  });
  return () => { unsubscribeQueries(); unsubscribeMutations(); };
}
